<template>
  <v-simple-table
    class="expert-tool__table anomalies"
    fixed-header
  >
    <template v-slot:default>
      <tbody>
        <tr
          v-for="(rule, i) in prescriptionRules"
          :key="`${rule.key}-${i}`"
        >
          <td class="expert-tool__table-checkbox-cell">
            <v-checkbox
              v-model="selectedRules"
              :value="rule.key"
              color="success"
            ></v-checkbox>
          </td>
          <td class="expert-tool__table-item-name">{{ $ucfirst($t(rule.name, { catalog: 'app_dashboard'})) }}</td>
          <td class="anomailes__heart-rate-cell">
            <v-text-field
              v-if="rule.key === 'ANOMALY__MYOCARDIAL_ISCHEMIC_THRESHOLD'"
              ref="ANOMALY__MYOCARDIAL_ISCHEMIC_THRESHOLD"
              data-rule="ANOMALY__MYOCARDIAL_ISCHEMIC_THRESHOLD"
              class="anomailes__heart-rate-input"
              :value="hrMyocardialIschemicTreshold"
              @blur="updateHrMyocardialIschemicTreshold"
              @click="autoToggleRule"
              @keyup="autoToggleRule"
              :label="$ucfirst($t('heartRate'))"
              suffix="bpm"
              hide-details
              outlined
              style="display: inline-flex"
              dense
              type="number"
              :min="0"
              @keypress="onKeyPressNumbers"
              @paste="onPasteNumbers"
            ></v-text-field>
            <v-text-field
              v-else-if="rule.key === 'ANOMALY__VENTRICULAR_TACHYCARDIA'"
              ref="ANOMALY__VENTRICULAR_TACHYCARDIA"
              data-rule="ANOMALY__VENTRICULAR_TACHYCARDIA"
              class="anomailes__heart-rate-input"
              :value="hrVentricularTachycardia"
              @blur="updateHrVentricularTachycardia"
              @click="autoToggleRule"
              @keyup="autoToggleRule"
              :label="$ucfirst($t('heartRate'))"
              suffix="bpm"
              hide-details
              outlined
              style="display: inline-flex"
              dense
              type="number"
              :min="0"
              @keypress="onKeyPressNumbers"
              @paste="onPasteNumbers"
            ></v-text-field>
            <v-text-field
              v-else-if="rule.key === 'ANOMALY__ICD_TRESHOLD'"
              ref="ANOMALY__ICD_TRESHOLD"
              data-rule="ANOMALY__ICD_TRESHOLD"
              class="anomailes__heart-rate-input"
              :value="hrICDTreshold"
              @blur="updateHrICDTreshold"
              @click="autoToggleRule"
              @keyup="autoToggleRule"
              :label="$ucfirst($t('heartRate'))"
              suffix="bpm"
              hide-details
              outlined
              style="display: inline-flex"
              dense
              type="number"
              :min="0"
              @keypress="onKeyPressNumbers"
              @paste="onPasteNumbers"
            ></v-text-field>
            <v-tooltip
              right
              v-if="!heartRateValid(rule) || !heartRatePositive(rule)"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  style="margin-left: 10px"
                >mdi-alert-circle</v-icon>
              </template>
              <span v-if="!heartRateValid(rule)">{{ $ucfirst($t('validation.IS_REQUIRED')) }}</span>
              <span v-else>{{ $ucfirst($t('validation.onlyPositiveNumbers')) }}</span>
            </v-tooltip>
          </td>
        </tr>
      </tbody>
    </template>
  </v-simple-table>
</template>

<script>
import { mapGetters } from 'vuex';

/**
 * Component Anomalies.vue
 *
 * @module Anomalies
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.9] app/src/APP_DASHBOARD/templates/views/ExpertTool/SportsGoalBoxes/Anomalies.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'Anomalies',
  props: {
    ruleType: {
      type: String,
      required: true,
    },
  },
  watch: {
    selectedRules: function (cur, prev) {
      // only when rule is being selected, not unselected
      if (prev.length >= cur.length || !cur || !cur.length) {
        return;
      }

      const key = this.selectedRules[this.selectedRules.length-1];
      if (this.$refs[key]) {
        const _ref = this.$refs[key][0].$refs.input;
        const filled = this.heartRateValid({key})

        setTimeout(() => {
          !filled ? _ref.focus() : _ref.select()
        }, 150);
      }
    }
  },
  computed: {
    ...mapGetters({
      hrAtrialFibrilationProvoked: 'expertTool/hrAtrialFibrilationProvoked',
      hrMyocardialIschemicTreshold: 'expertTool/hrMyocardialIschemicTreshold',
      hrVentricularTachycardia: 'expertTool/hrVentricularTachycardia',
      hrICDTreshold: 'expertTool/hrICDTreshold',
    }),
    prescriptionRules() {
      return this.$store.getters['expertTool/prescriptionRules']
        .filter((item) => item.type === this.ruleType);
    },
    selectedRules: {
      get() {
        return this.$store.getters['expertTool/selectedRules'];
      },
      set(selected) {
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
        return this.$store.dispatch('expertTool/updateSelectedRules', selected);
      },
    },
  },
  methods: {
    updateHrAtrialFibrilationProvoked({ target }) {
      this.$store.dispatch('expertTool/updateHrAtrialFibrilationProvoked', target.value);
      this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
    },
    updateHrMyocardialIschemicTreshold({ target }) {
      this.$store.dispatch('expertTool/updateHrMyocardialIschemicTreshold', target.value);
      this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
    },
    updateHrVentricularTachycardia({ target }) {
      this.$store.dispatch('expertTool/updateHrVentricularTachycardia', target.value);
      this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
    },
    updateHrICDTreshold({ target }) {
      this.$store.dispatch('expertTool/updateHrICDTreshold', target.value);
      this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
    },
    autoToggleRule({ target, key }) {
      const rule = target.getAttribute('data-rule');
      const index = this.selectedRules.indexOf(rule);

      if (target.value == '') {
        // Do not update on click event - check for key
        if (index > -1 && key) {
          this.selectedRules.splice(index, 1);
        }
      } else if (index == -1) {
        this.selectedRules.push(rule);
      }
    },
    heartRateValid(rule) {
      const index = this.selectedRules.indexOf(rule.key);
      if (index === -1) {
        return true;
      }
      switch (rule.key) {
        case 'ANOMALY__MYOCARDIAL_ISCHEMIC_THRESHOLD':
          return this.hrMyocardialIschemicTreshold !== '';
        case 'ANOMALY__VENTRICULAR_TACHYCARDIA':
          return this.hrVentricularTachycardia !== '';
        case 'ANOMALY__ICD_TRESHOLD':
          return this.hrICDTreshold !== '';
        default:
          return true;
      }
    },
    heartRatePositive(rule) {
      const index = this.selectedRules.indexOf(rule.key);
      if (index === -1 || !this.heartRateValid(rule)) {
        return true;
      }
      switch (rule.key) {
        case 'ANOMALY__MYOCARDIAL_ISCHEMIC_THRESHOLD':
          return +this.hrMyocardialIschemicTreshold > 0;
        case 'ANOMALY__VENTRICULAR_TACHYCARDIA':
          return +this.hrVentricularTachycardia > 0;
        case 'ANOMALY__ICD_TRESHOLD':
          return +this.hrICDTreshold > 0;
        default:
          return true;
      }
    },
    onKeyPressNumbers(event) {
      const onlyNumbers = /\d/
      if (!onlyNumbers.test(event.key)) {
        event.preventDefault();
      }
    },
    onPasteNumbers(event) {
      const onlyNumbers = /^\d+$/
      const pasted = event.clipboardData.getData('text/plain')
      if (!onlyNumbers.test(pasted)) {
        event.preventDefault();
      }
    }
  },
};
</script>

<style lang="scss">
.anomalies {
  .expert-tool__table-item-name {
    white-space: nowrap;
  }
}

.anomailes__heart-rate-cell {
  width: 99%;
}

.anomailes__heart-rate-input {
  width: 300px;
}
</style>
